import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldInstance } from '../FieldInstance';
import { Validator } from '../FieldRenderProps';
import { useFormContext } from '../../Form/Context';
import { useFallbackId } from '../../useFallbackId';

export type UseFieldProps<T> = {
    name: string;

    id?: string;

    description?: string;

    initialValue?: T;

    onChangeValidate?: Validator<T>;

    onBlurValidate?: Validator<T>;

    onSubmitValidate?: Validator<T>;

    required?: boolean;

    /**
     * Does the field value satisfy a required field
     */
    isFulfilled?: (value: T) => boolean;
};

export const useField = <T>({
    id,
    name,
    initialValue,
    description,
    required = false,
    onChangeValidate,
    onBlurValidate,
    onSubmitValidate,
    isFulfilled,
}: UseFieldProps<T>): FieldInstance<T> => {
    const { setField, removeField, getField, initialValues, requiredStrategy } =
        useFormContext<Record<string, any>>();
    const initialFieldInstance = useMemo(() => {
        return getField(name);
    }, []);

    const [value, _setValue] = useState<T | null>(
        initialFieldInstance?.value ?? initialValues[name] ?? initialValue ?? null
    );

    const [isDirty, _setIsDirty] = useState<boolean>(false);

    const [isValidating, _setIsValidating] = useState<boolean>(false);

    const [errors, _setErrors] = useState<string[]>(initialFieldInstance?.errors ?? []);

    const [isTouched, _setIsTouched] = useState<boolean>(initialFieldInstance?.isTouched ?? false);

    const fieldId = useFallbackId(id);

    const hasMounted = useRef(false);

    const setValue = useCallback(
        (val: T | null) => {
            _setValue(val);

            _setIsDirty(val !== initialValue);
        },
        [_setIsDirty, initialValue]
    );

    const runValidation = useCallback(
        async (validator: Validator<T>): Promise<boolean> => {
            if (value === null) {
                _setErrors([]);
                return true;
            }

            _setIsValidating(true);

            try {
                const result = await validator(value);

                if (result.pass) {
                    _setErrors([]);

                    return true;
                }

                _setErrors([result.message]);

                return false;
            } catch (err) {
                console.error('Failed to run validator', err);

                _setErrors([]);

                return true;
            } finally {
                _setIsValidating(false);
            }
        },
        [value]
    );

    const isFulfilledValidate = useCallback<Validator<T | null>>(async () => {
        if (!required) {
            return { pass: true };
        }

        const FAIL = { pass: false, message: 'Field is required' };

        if (value === null) {
            return FAIL;
        }

        if (isFulfilled) {
            if (isFulfilled(value)) {
                return { pass: true };
            }

            return FAIL;
        }

        if (value === undefined || value === null || value === '') {
            return FAIL;
        }

        return { pass: true };
    }, [value, isFulfilled, required]);

    const runFulfilledValidation = useCallback(async () => {
        _setIsValidating(true);

        try {
            const result = await isFulfilledValidate(value);

            if (result.pass) {
                _setErrors([]);

                return true;
            }

            _setErrors([result.message]);

            return false;
        } catch (err) {
            console.error('Failed to run validator', err);

            _setErrors([]);

            return true;
        } finally {
            _setIsValidating(false);
        }
    }, [value, isFulfilledValidate]);

    const validateOnChange = useCallback(async () => {
        if (onChangeValidate) {
            return runValidation(onChangeValidate);
        }
    }, [runValidation]);

    const validateOnBlur = useCallback(async () => {
        const isFulfilledResult = await runFulfilledValidation();

        if (!isFulfilledResult) {
            return false;
        }

        if (onBlurValidate) {
            return runValidation(onBlurValidate);
        }
    }, [runValidation, runFulfilledValidation]);

    const validateOnSubmit = useCallback(async (): Promise<boolean> => {
        const isFulfilledResult = await runFulfilledValidation();

        if (!isFulfilledResult) {
            return false;
        }

        if (onSubmitValidate) {
            return runValidation(onSubmitValidate);
        }

        return true;
    }, [runValidation, runFulfilledValidation]);

    const setErrors = useCallback((errors: string[]) => {
        _setErrors(errors);
    }, []);

    useEffect(() => {
        if (hasMounted.current === false) {
            return;
        }

        validateOnChange();
    }, [value]);

    const onBlur = useCallback(async () => {
        _setIsTouched(true);
        await validateOnBlur();
    }, [_setIsTouched, validateOnBlur]);

    const isValid = useMemo(() => {
        return errors.length === 0;
    }, [errors]);

    const errorId = useMemo(() => {
        if (isValid) {
            return undefined;
        }

        return `${fieldId}-error`;
    }, [fieldId, isValid]);

    const descriptionId = useMemo(() => {
        if (!description) {
            return undefined;
        }
        return `${fieldId}-description`;
    }, [description, fieldId]);

    const instance = useMemo<FieldInstance<T>>(
        () => ({
            name,
            value,
            errors,
            setValue,
            setErrors,
            onBlur,
            validateOnSubmit,
            isTouched,
            isDirty,
            isValid,
            isValidating,
            required,
            requiredStrategy,
            errorId,
            descriptionId,
            fieldId,
        }),
        [
            name,
            value,
            errors,
            setValue,
            setErrors,
            onBlur,
            validateOnSubmit,
            isTouched,
            isDirty,
            isValid,
            isValidating,
            required,
            requiredStrategy,
            errorId,
            descriptionId,
            fieldId,
        ]
    );

    useEffect(() => {
        setField(instance);
        return () => removeField(instance);
    }, [setField, removeField, instance]);

    useEffect(() => {
        hasMounted.current = true;
    }, []);

    return instance;
};
